import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const ThemeContext = createContext(null);

/**
 * Light mode is the default for new visitors.
 * - localStorage 'sr_theme' persists across logout/login on the same device.
 * - When the user is authenticated we also sync to /api/me/theme so the
 *   preference follows them across devices.
 */
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "light";
    const saved = localStorage.getItem("sr_theme");
    return saved === "dark" || saved === "light" ? saved : "light"; // default = LIGHT
  });

  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    try { localStorage.setItem("sr_theme", theme); } catch { /* ignore */ }
  }, [theme]);

  // Pull theme from server when a token exists (e.g. fresh login on a new device).
  useEffect(() => {
    let cancelled = false;
    const tok = typeof window !== "undefined" ? localStorage.getItem("sr_token") : null;
    if (!tok) return undefined;
    api.get("/me/theme").then((r) => {
      if (cancelled) return;
      const remote = r?.data?.theme;
      if ((remote === "light" || remote === "dark") && remote !== theme) {
        setTheme(remote);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
    // intentionally only runs once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistRemote = (next) => {
    const tok = typeof window !== "undefined" ? localStorage.getItem("sr_token") : null;
    if (!tok) return;
    api.put("/me/theme", { theme: next }).catch(() => {});
  };

  const toggle = () => {
    setTheme((t) => {
      const next = t === "dark" ? "light" : "dark";
      persistRemote(next);
      return next;
    });
  };

  const setThemeExplicit = (next) => {
    if (next !== "light" && next !== "dark") return;
    setTheme(next);
    persistRemote(next);
  };

  return (
    <ThemeContext.Provider value={{ theme, toggle, setTheme: setThemeExplicit }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
